import { useEffect, useState } from "react";
import { useAuthContext } from "@asgardeo/auth-react";
import { clearRestriction, getRestriction, setRestriction, subscribeToRestriction } from "../../services/rate-limit";

const operationName = {
  case_search: "case-law search",
  dictionary: "dictionary search",
  reports: "reports search",
  summary: "case-summary access",
};

export default function RateLimitNotice() {
  const { state, getAccessToken } = useAuthContext();
  const [restriction, setCurrent] = useState(getRestriction);

  useEffect(() => subscribeToRestriction(setCurrent), []);

  useEffect(() => {
    // An account can be locked by a request made outside this browser (for
    // example, another tab or API client). Authenticated sessions must always
    // query the server so a fresh page load can discover that state even when
    // localStorage does not contain a previous warning.
    let cancelled = false;
    const refresh = async () => {
      try {
        let token = "";
        if (state.isAuthenticated) token = (await getAccessToken()) || "";
        const response = await fetch("/api/rate-limit/status", {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          cache: "no-store",
        });
        if (!response.ok || cancelled) return;
        const status = await response.json();
        if (!status.warned && !status.locked) {
          clearRestriction();
        } else if (
          !restriction ||
          status.locked !== restriction.locked ||
          status.warned !== restriction.warned ||
          status.operation !== restriction.operation
        ) {
          setRestriction({
            ...(restriction || {}),
            ...status,
            code: status.locked ? "ACCOUNT_LOCKED" : "RATE_LIMIT_WARNING",
            message: status.locked
              ? "Your account has been locked because activity continued after a usage-limit warning. Please contact Paralegal support."
              : "You have reached the permitted usage limit. The requested action was not performed. Access will reset automatically; further attempts may lock an authenticated account.",
            reset_at:
              !status.locked && status.retry_after_seconds
                ? Math.floor(Date.now() / 1000) + status.retry_after_seconds
                : null,
          });
        }
      } catch {
        // Keep the current server-issued restriction until the next poll.
      }
    };
    refresh();
    if (!restriction && !state.isAuthenticated) {
      return () => {
        cancelled = true;
      };
    }
    const timer = window.setInterval(refresh, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [getAccessToken, restriction?.locked, restriction?.reset_at, state.isAuthenticated]);

  if (!restriction) return null;
  const reset = restriction.reset_at
    ? new Date(restriction.reset_at * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 px-4" role="alertdialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-pl-lg border border-pl-border bg-pl-bg-elevated p-6 shadow-xl">
        <h2 className="text-xl font-display font-semibold text-pl-text-primary">
          {restriction.locked ? "Account access locked" : "Usage limit reached"}
        </h2>
        <p className="mt-3 text-14 leading-6 text-pl-text-secondary">
          {restriction.message || "The requested action was not performed because the usage limit was reached."}
        </p>
        {restriction.operation && (
          <p className="mt-2 text-13 text-pl-text-tertiary">Affected service: {operationName[restriction.operation] || restriction.operation}</p>
        )}
        {!restriction.locked && reset && (
          <p className="mt-2 text-13 text-pl-text-tertiary">Access is expected to reset at {reset}.</p>
        )}
      </div>
    </div>
  );
}
